import { range } from "@thi.ng/iterators";
import stringify from "fast-json-stable-stringify";
import { InsightDataPropertyFactory, DatasetMetadata, LabelFactory, CountOutcomeMetadata } from "utils/insight/metadata";
import { Summary, SummaryFactory } from "utils/insight/summary";
import { Conditions, conditionsEquals, conditionsIsEmpty, getConditionByIndex, getConditionIds } from "utils/insight/conditions";
import { DataProperties, InsightConditionsBuilder } from "utils/insight/properties";
import { filterUndefined, getNumericComparator, reverseOrder, chainComparators, keysOf } from "utils/collect";
import { InsightDataFactoryProvider, filterDescriptiveValues } from "utils/insight/data";
import { AssociationFactory } from "utils/insight/association";
import { ModelFeatureSetFactory } from "utils/insight/models";
import { InsightChartFactoryProvider } from "utils/insight/charts";
export var RelatedGoal;
(function (RelatedGoal) {
    RelatedGoal["Best"] = "best";
    RelatedGoal["Worst"] = "worst";
})(RelatedGoal || (RelatedGoal = {}));
const DEFAULT_OPTIONS = {
    limit: 18,
    goal: RelatedGoal.Best
};
export const DEFAULT_MIN_EXPLORE_SAMPLE_SIZE = 10;
function isInsightMetadata(x) {
    return x !== undefined && "type" in x && "properties" in x;
}
function adjustRSquared(r2, n, p) {
    return 1 - (1 - r2) * ((n - 1) / (n - p - 1));
}
function getAdjustedRSquared(stats) {
    let n = 0, sum = 0, sumOfSquares = 0;
    for (let i = 0; i < stats.length; i++) {
        n += stats[i].n;
        sum += stats[i].sum;
        sumOfSquares += stats[i].sumOfSquares;
    }
    const global = n === 0 ? 0 : sumOfSquares - (sum * sum) / n;
    let resSS = 0;
    for (let i = 0; i < stats.length; i++) {
        resSS += stats[i].getTotalSumOfSquares();
    }
    const r2 = resSS == global ? 0 : 1 - resSS / global;
    return adjustRSquared(r2, n, stats.length - 1);
}
export class InsightFactory {
    metadata;
    summaryFactory;
    insightConditionsFactory;
    dataFactoryProvider;
    insightScoreFactory;
    insightChartFactoryProvider;
    insightNarrativeFactory;
    dataPropertyFactory;
    associationFactory;
    maxOrder;
    comparisonOptions;
    cache = {};
    firstOrderScores = undefined;
    combinationCount = undefined;
    constructor(metadata, summaryFactory, insightConditionsFactory, dataFactoryProvider, insightScoreFactory, insightChartFactoryProvider, insightNarrativeFactory, dataPropertyFactory, associationFactory, maxOrder, comparisonOptions) {
        this.metadata = metadata;
        this.summaryFactory = summaryFactory;
        this.insightConditionsFactory = insightConditionsFactory;
        this.dataFactoryProvider = dataFactoryProvider;
        this.insightScoreFactory = insightScoreFactory;
        this.insightChartFactoryProvider = insightChartFactoryProvider;
        this.insightNarrativeFactory = insightNarrativeFactory;
        this.dataPropertyFactory = dataPropertyFactory;
        this.associationFactory = associationFactory;
        this.maxOrder = maxOrder;
        this.comparisonOptions = comparisonOptions;
    }
    _getInsightType(query) {
        if (query === undefined) {
            return "Descriptive";
        }
        else {
            return query.type || "Descriptive";
        }
    }
    getCombinationCount() {
        if (!this.metadata) {
            return 0;
        }
        if (this.combinationCount !== undefined) {
            return this.combinationCount;
        }
        const summaryCombinationCount = this.summaryFactory.getCombinationCount();
        let count = 1; // add one for overall
        const dims = this.metadata.getColumns().map((c) => c.getLevelCount());
        for (let i = 0; i < dims.length; i++) {
            count += dims[i]; // first order
            for (let j = i + 1; j < dims.length; j++) {
                count += dims[i] * dims[j]; // second order
            }
        }
        this.combinationCount = Math.max(count, summaryCombinationCount);
        return this.combinationCount;
    }
    getAllCorrelations() {
        const corrs = this.associationFactory.getAllPairwiseAssociations();
        const n = this.metadata.getColumns().length;
        const names = [...range(n)].map((i) => this.metadata.getColumn(i).getName());
        const result = [];
        for (let i = 0; i < n; i++) {
            for (let j = i + 1; j < n; j++) {
                result.push({
                    left: names[i],
                    right: names[j],
                    value: corrs[i][j]
                });
            }
        }
        return result;
    }
    getSummary(filters) {
        const conditions = this.insightConditionsFactory.getInsightConditions({
            filters
        });
        return this.summaryFactory.getSummary(Conditions.from(conditions.conditions));
    }
    addTables(tables) {
        this.summaryFactory.addTables(tables, this.metadata);
    }
    getMissingSetIds(query) {
        const type = this._getInsightType(query);
        const insightConditions = this.insightConditionsFactory.getInsightConditions(query);
        const related = this._getRelatedInsights(insightConditions, type);
        const idSets = [insightConditions].concat(related).map(({ ids, conditions }) => {
            return [...new Set((ids ?? []).concat(conditions === undefined ? [] : [...conditions].map((c) => c.id)))];
        });
        return [...new Set(idSets.map((x) => JSON.stringify(x.sort())))]
            .map((x) => JSON.parse(x))
            .filter((ids) => ids.length <= 3 && !this.summaryFactory.hasSummary(ids));
    }
    getPropertyMetadata() {
        return {
            outcome: this.metadata.getOutcome(),
            columns: this.metadata.getColumns().map((column, id) => ({
                column: this.dataPropertyFactory.getColumnDataProperty(id),
                values: [...range(column.getLevelCount())].map((value) => this.dataPropertyFactory.getDataProperty(Conditions.create(id, value)))
            }))
        };
    }
    getValidProperties(properties) {
        const builder = this.insightConditionsFactory.getBuilder();
        return properties.filter((p) => builder.isValidProperty(p));
    }
    getInsight(query) {
        if (isInsightMetadata(query)) {
            const key = stringify(query);
            if (this.cache[key]) {
                return this.cache[key];
            }
        }
        const insightConditions = this.insightConditionsFactory.getInsightConditions(query);
        const type = this._getInsightType(query);
        const data = this.dataFactoryProvider.getInsightDataFactory(type).getInsightData(insightConditions);
        if (data === undefined) {
            return undefined;
        }
        return this._getInsight(data);
    }
    getInsights(query, options = DEFAULT_OPTIONS) {
        const maxInsights = options.limit ?? DEFAULT_OPTIONS.limit;
        if (query === undefined) {
            return this.getInsights({})
                .slice(0, maxInsights)
                .flatMap((insight) => [insight, ...this.getInsights(insight.metadata).slice(0, 3)]);
        }
        const insightConditions = this.insightConditionsFactory.getInsightConditions(query);
        const type = this._getInsightType(query);
        const dataFactory = this.dataFactoryProvider.getInsightDataFactory(type);
        const relatedInsights = this._getRelatedInsights(insightConditions, type);
        const validInsights = relatedInsights.filter((x) => this.insightScoreFactory.isValidCandidate(x, type));
        const related = validInsights.map((x) => dataFactory.getInsightData(x)).filter(filterUndefined);
        if (type === "Difference") {
            const scoredInsights = related
                .map((data) => ({
                score: this.insightScoreFactory.getScore(data),
                data: data
            }))
                .filter(({ score }) => isFinite(score));
            const comparator = getNumericComparator((x) => -x.score);
            const sortedScoredRelated = scoredInsights.sort(comparator);
            const sortedRelated = sortedScoredRelated.map((x) => ({ ...x.data, score: x.score }));
            return sortedRelated
                .map((data) => this._getInsight(data, data.score))
                .filter(filterUndefined)
                .slice(0, maxInsights);
        }
        const scoredInsights = related
            .map((data) => ({
            score: this.insightScoreFactory.getScore(data),
            data: data
        }))
            .filter(({ score }) => isFinite(score));
        let comparator = getNumericComparator((x) => -x.score);
        if (options.goal === RelatedGoal.Worst) {
            comparator = reverseOrder(comparator);
        }
        const sortedScoredRelated = scoredInsights.sort(comparator);
        /*
            const scoreCutoff = type === "Descriptive" ?
                (sortedScoredRelated[0]?.score ?? 0) * .1 : -Infinity;
            */
        const scoreCutoff = 0;
        const sortedRelated = sortedScoredRelated
            .filter((x) => x.score >= scoreCutoff)
            .map((x) => ({ ...x.data, score: x.score }));
        return sortedRelated
            .map((data) => this._getInsight(data, data.score))
            .filter(filterUndefined)
            .slice(0, maxInsights);
    }
    _getInsight(data, score = 0) {
        const id = data.id;
        const metadata = {
            type: data.type,
            properties: this.dataPropertyFactory.getInsightDataProperties({
                ids: [id],
                conditions: data.conditions
            })
        };
        if (data.type === "Correlation") {
            metadata.type = "Descriptive";
        }
        const chartFactory = this.insightChartFactoryProvider.getChartFactory(data.type);
        const narrative = this.insightNarrativeFactory.getNarrative(data);
        const insight = {
            metadata: metadata,
            chart: chartFactory.getChart(data),
            narrative: narrative,
            score: score !== undefined ? score : 0
        };
        this.cache[stringify(metadata)] = insight;
        return insight;
    }
    _getRelatedInsights(insightConditions, type = "Descriptive") {
        const ids = insightConditions.ids;
        const conditions = [...(insightConditions.conditions || [])];
        if (type == "Correlation") {
            if (ids?.length === 1) {
                const id = ids[0];
                return this.metadata
                    .getColumns()
                    .flatMap((_, i) => (i === id ? [] : [{ ids: [id, i], conditions: conditions }]));
            }
        }
        else if (type == "ModelDriver" && (ids?.length === 1 || conditions.length > 0)) {
            if (ids?.length === 1) {
                const id = ids[0];
                return this.metadata.getConditionSets(id).map((conditions) => ({
                    ids: [id],
                    conditions: conditions
                }));
            }
            else {
                return conditions.map((c) => ({
                    ids: [c.id],
                    conditions: Conditions.singleton(c)
                }));
            }
        }
        else if (type == "Difference") {
            const conditionsById = {};
            for (const c of conditions) {
                if (conditionsById[c.id] === undefined) {
                    conditionsById[c.id] = [c];
                }
                else {
                    conditionsById[c.id].push(c);
                }
            }
            let xIds = ids ?? [];
            let diffIds = [];
            if (ids !== undefined && this.comparisonOptions !== undefined && this.comparisonOptions.dateField !== undefined) {
                const ic = this.insightConditionsFactory.getInsightConditionsFromProperties([
                    DataProperties.column(this.comparisonOptions.dateField)
                ]);
                const timestampId = ic.ids?.[0];
                if (timestampId !== undefined) {
                    diffIds = ids.filter((id) => timestampId != id && conditionsById[id] === undefined);
                    xIds = [timestampId];
                }
            }
            const related = [];
            for (const id of xIds) {
                if (this.maxOrder <= 1) {
                    related.push({
                        ids: [id],
                        conditions: Conditions.emptySet()
                    });
                }
                else if (diffIds.length == 0 && keysOf(conditionsById).length == 0) {
                    for (const cId of range(this.metadata.getColumns().length)) {
                        if (cId == id) {
                            continue;
                        }
                        for (const value of range(this.metadata.getColumn(cId).getLevelCount())) {
                            related.push({
                                ids: [id],
                                conditions: [Conditions.create(cId, value)]
                            });
                        }
                    }
                }
                else if (diffIds.length == 0) {
                    const groupedConditions = Object.values(conditionsById);
                    if (this.maxOrder > 2 && groupedConditions.length > 1) {
                        for (let i = 0; i < groupedConditions.length; i++) {
                            for (let j = i + 1; j < groupedConditions.length; j++) {
                                for (const first of groupedConditions[i]) {
                                    for (const second of groupedConditions[j]) {
                                        related.push({
                                            ids: [id],
                                            conditions: [first, second]
                                        });
                                    }
                                }
                            }
                        }
                    }
                    else if (this.maxOrder > 1) {
                        for (const group of groupedConditions) {
                            for (const c of group) {
                                related.push({
                                    ids: [id],
                                    conditions: [c]
                                });
                            }
                        }
                    }
                }
                else if (Object.keys(conditionsById).length > 0 && this.maxOrder > 2) {
                    for (const cId of keysOf(conditionsById)) {
                        for (const dId of diffIds) {
                            if (cId.toString() == dId.toString()) {
                                continue;
                            }
                            for (const c of conditionsById[cId]) {
                                for (const value of range(this.metadata.getColumn(dId).getLevelCount())) {
                                    related.push({
                                        ids: [id],
                                        conditions: [Conditions.create(dId, value), c]
                                    });
                                }
                            }
                        }
                    }
                }
                else {
                    for (const dId of diffIds) {
                        if (conditionsById[dId] == undefined) {
                            conditionsById[dId] = [];
                        }
                        else {
                            continue;
                        }
                        for (const value of range(this.metadata.getColumn(dId).getLevelCount())) {
                            conditionsById[dId].push(Conditions.create(dId, value));
                        }
                    }
                    const groupedConditions = Object.values(conditionsById);
                    if (this.maxOrder > 2 && groupedConditions.length > 1) {
                        for (let i = 0; i < groupedConditions.length; i++) {
                            for (let j = i + 1; j < groupedConditions.length; j++) {
                                for (const first of groupedConditions[i]) {
                                    for (const second of groupedConditions[j]) {
                                        related.push({
                                            ids: [id],
                                            conditions: [first, second]
                                        });
                                    }
                                }
                            }
                        }
                    }
                    else if (this.maxOrder > 1) {
                        for (const group of groupedConditions) {
                            for (const c of group) {
                                related.push({
                                    ids: [id],
                                    conditions: [c]
                                });
                            }
                        }
                    }
                }
                return related;
            }
        }
        else {
            if (ids === undefined) {
                return this.metadata.getColumns().flatMap((_, i) => conditions.length === 0
                    ? [{ ids: [i] }]
                    : conditions.map((c) => ({
                        ids: [i],
                        conditions: Conditions.singleton(c)
                    })));
            }
            else if (conditions.length === 0 && ids?.length === 1) {
                const id = ids[0];
                return this.metadata.getColumns().flatMap((c, i) => i === id
                    ? []
                    : [...range(c.getLevelCount())].map((value) => ({
                        ids,
                        conditions: Conditions.single(i, value)
                    })));
            }
            else if (conditions.length === 0 && ids?.length === 2) {
                const [first, second] = ids;
                const firstDrill = [...range(this.metadata.getColumn(first).getLevelCount())].map((value) => ({
                    ids: [second],
                    conditions: Conditions.single(first, value)
                }));
                const secondDrill = [...range(this.metadata.getColumn(second).getLevelCount())].map((value) => ({
                    ids: [first],
                    conditions: Conditions.single(second, value)
                }));
                return firstDrill.concat(secondDrill);
            }
            else if (ids?.length === 1) {
                const id = ids[0];
                return conditions.length === 0
                    ? [{ ids: [id] }]
                    : conditions.map((c) => ({
                        ids: [id],
                        conditions: Conditions.singleton(c)
                    }));
            }
            else if (ids !== undefined) {
                if (ids.length === 0) {
                    return this._getRelatedInsights({ conditions });
                }
                return ids.flatMap((id) => this._getRelatedInsights({ ids: [id], conditions }));
            }
        }
        return [{ ids, conditions }];
    }
    static create(dataset, raw, inclusionProbabilities, interactionInclusionProbabilities, job, outcomeOverride, comparisonOptions, exploreOptions) {
        let target = outcomeOverride;
        if (target === undefined) {
            target =
                job?.id === undefined
                    ? raw.target
                    : {
                        name: job.outcome,
                        success_value: job.outcome_value
                    };
        }
        if (typeof raw.target.success_value === "number" &&
            typeof target?.success_value === "string" &&
            !isNaN(Number(target?.success_value))) {
            target = {
                ...target,
                success_value: Number(target.success_value)
            };
        }
        else if (typeof raw.target.success_value === "boolean" && typeof target.success_value === "string") {
            target = {
                ...target,
                success_value: target.success_value.toLowerCase() == "true"
            };
        }
        else if (raw.target.name != target.name || typeof raw.target.success_value !== typeof target.success_value) {
            target = raw.target;
        }
        const minSampleSize = exploreOptions?.minSampleSize ?? DEFAULT_MIN_EXPLORE_SAMPLE_SIZE;
        const key = `${dataset.id}-${dataset.updated_date}-${target.name}-${target.success_value}-${job?.id}-${Object.keys(job?.models ?? {}).length}-${JSON.stringify(comparisonOptions)}-${minSampleSize}`;
        if (CACHE !== undefined && key == CACHE_KEY) {
            return CACHE;
        }
        let metadata = DatasetMetadata.create(target, raw.columns);
        let summaryFactory = SummaryFactory.create(raw, metadata);
        let needsRefresh = false;
        for (let i = 0; i < raw.columns.length; i++) {
            const c = raw.columns[i];
            const datasetCounts = dataset.categorical_counts.find(([name, _]) => name == c.name);
            const f = dataset.fields[c.name];
            if (c.type == "categorical" && f.type == "categorical" && c.levels.length > 1 && datasetCounts !== undefined) {
                const cOtherIndex = c.levels.findIndex((x) => x == "{Other}");
                const badOtherIndex = c.levels[c.levels.length - 1] == "{Missing}" ? c.levels.length - 2 : c.levels.length - 1;
                if (cOtherIndex < 0 || cOtherIndex == badOtherIndex) {
                    continue;
                }
                const expected = datasetCounts[1].find(([level, _]) => level == "{Other}")?.[1];
                if (expected === undefined) {
                    continue;
                }
                const s = summaryFactory.getSummary(Conditions.single(i, cOtherIndex));
                if (expected !== s?.n) {
                    const so = summaryFactory.getSummary(Conditions.single(i, badOtherIndex));
                    if (expected === so?.n) {
                        const updated = c.levels.filter((l) => l != "{Other}" && l != "{Missing}");
                        updated.push("{Other}");
                        if (c.levels[c.levels.length - 1] == "{Missing}") {
                            updated.push("{Missing}");
                        }
                        c.levels = updated;
                        needsRefresh = true;
                    }
                }
            }
        }
        if (needsRefresh) {
            metadata = DatasetMetadata.create(target, raw.columns);
            summaryFactory = SummaryFactory.create(raw, metadata);
        }
        const labelFactory = LabelFactory.create(metadata);
        const featuresSet = new ModelFeatureSetFactory(metadata).getModelTrainingJobFeatureSet(job);
        const insightConditionsFactory = InsightConditionsFactory.create(metadata);
        const associationFactory = new AssociationFactory(summaryFactory);
        const dataFactoryProvider = InsightDataFactoryProvider.create(metadata, summaryFactory, associationFactory, featuresSet, minSampleSize);
        const insightScoreFactory = InsightScoreFactory.create(metadata, inclusionProbabilities, interactionInclusionProbabilities, dataFactoryProvider.getInsightDataFactory("Descriptive"), minSampleSize);
        const insightChartFactoryProvider = InsightChartFactoryProvider.create(metadata, minSampleSize);
        const insightNarrativeFactory = new InsightNarrativeFactory(metadata, labelFactory, featuresSet, minSampleSize);
        const dataPropertyFactory = new InsightDataPropertyFactory(metadata);
        const factory = new InsightFactory(metadata, summaryFactory, insightConditionsFactory, dataFactoryProvider, insightScoreFactory, insightChartFactoryProvider, insightNarrativeFactory, dataPropertyFactory, associationFactory, exploreOptions?.maxOrder ?? summaryFactory.getMaxOrder(), comparisonOptions);
        CACHE = factory;
        CACHE_KEY = key;
        return factory;
    }
}
let CACHE = undefined;
let CACHE_KEY = undefined;
function getVoteComparator(data) {
    return chainComparators([
        getNumericComparator((x) => {
            const positiveRate = x.positiveCount / x.totalCount;
            return Math.min(positiveRate, 1 - positiveRate);
        }),
        getNumericComparator((x) => {
            for (const value of data.values) {
                if (conditionsEquals(value.conditions, x.conditions)) {
                    return -1 * Math.abs(value.summary.getMean() - value.other.getMean());
                }
            }
            return 0;
        })
    ]);
}
class InsightNarrativeFactory {
    metadata;
    labelFactory;
    features;
    minValueCount;
    constructor(metadata, labelFactory, features, minValueCount) {
        this.metadata = metadata;
        this.labelFactory = labelFactory;
        this.features = features;
        this.minValueCount = minValueCount;
    }
    cleanInsight(data) {
        switch (data.type) {
            case "Descriptive":
            case "Correlation":
                return {
                    ...data,
                    values: filterDescriptiveValues(this.metadata, data, this.minValueCount)
                };
            case "Difference":
            default:
                return data;
        }
    }
    getNarrative(data) {
        data = this.cleanInsight(data);
        switch (data.type) {
            case "ModelDriver":
                return this.getModelDriverNarrative(data);
            case "Difference":
                return this.getDifferenceNarrative(data);
            case "Correlation":
            case "Descriptive":
            default:
                return this.getDescriptiveNarrative(data);
        }
    }
    getModelDriverNarrative(data) {
        const lf = this.labelFactory;
        const title = `${lf.getColumnLabel(data.id)}` +
            (conditionsIsEmpty(data.conditions)
                ? " (Model Drivers)"
                : `: ${lf.getValueLabels(data.conditions)} (Model Drivers)`);
        let body = "";
        let bodyComponents = [];
        let topText = "";
        if (!conditionsIsEmpty(data.conditions) && data.models.length > 0) {
            const votes = this.getModelVotes(data.conditions);
            topText = this.getModelVoteNarrative(votes);
            body += this.getModelVoteNarrative(votes);
            bodyComponents = this.getModelVoteNarrativeComponents(votes);
            let lowestModel;
            let highestModel;
            let highestValue = -Infinity;
            let lowestValue = Infinity;
            for (const model of data.models) {
                for (const feature of model.features) {
                    if (feature.value > highestValue) {
                        highestValue = feature.value;
                        highestModel = model.modelName;
                    }
                    if (feature.value < lowestValue) {
                        lowestValue = feature.value;
                        lowestModel = model.modelName;
                    }
                }
            }
            bodyComponents.push(`. The driver with the lowest`);
            bodyComponents.push(`was ${lowestModel} and the highest`);
            bodyComponents.push(`was ${highestModel}.`);
            body += `. The driver with the lowest value was ${lowestModel} and the highest value was ${highestModel}.`;
        }
        return {
            title: title,
            body: body,
            bodyComponents,
            columnLabel: lf.getColumnLabel(data.id),
            conditionsLabel: lf.getSetColumnLabels(data.conditions),
            topText: topText,
            expandText: undefined,
            summaryValue: undefined
        };
    }
    getDifferenceNarrative(data) {
        const { id, conditions } = data;
        const lf = this.labelFactory;
        const title = conditionsIsEmpty(conditions) ? lf.getColumnLabel(id) : `${lf.getSetLabel(conditions)}`;
        const bodyComponents = this.getBodyComponents(data);
        const bulletsComponents = this.getDescriptiveComponents(data);
        const sorted = [...data.values].filter((s) => s.summary.getTotalN() > 0);
        // 1 / is a hack to reverse this
        sorted.sort(getNumericComparator((x) => 1 / Math.abs(x.other.getMean() - x.summary.getMean())));
        const top = sorted?.[0];
        const fCondition = top === undefined ? undefined : getConditionByIndex(top.conditions, 0);
        const rowCount = data.values.reduce((a, b) => a + b.summary.getTotalN() + b.other.getTotalN(), 0);
        let expandText;
        expandText = [];
        sorted
            .filter((s, i) => i > 0 && i < 5)
            .forEach((s, i) => {
            expandText.push(this.getExpandText(data, s, rowCount, i === 3));
        });
        return {
            title: title,
            body: this.getBody(data),
            bodyComponents,
            bullets: this.getDescriptiveBullets(data),
            bulletsComponents,
            columnLabel: lf.getColumnLabel(data.id),
            conditionsLabel: lf.getSetLabel(data.conditions),
            topText: this.getTopText(data, top, rowCount),
            expandText: expandText,
            summaryValue: fCondition !== undefined ? this.labelFactory.getRawValue(fCondition.id, fCondition.value) : undefined
        };
    }
    getDescriptiveNarrative(data) {
        const { id, conditions } = data;
        const lf = this.labelFactory;
        const title = lf.getColumnLabel(id) + (conditionsIsEmpty(conditions) ? "" : ` when ${lf.getSetLabel(conditions)}`);
        const bodyComponents = this.getBodyComponents(data);
        const bulletsComponents = this.getDescriptiveComponents(data);
        const sorted = [...data.values].filter((s) => s.summary.getTotalN() > 0);
        // 1 / is a hack to reverse this
        sorted.sort(getNumericComparator((x) => 1 / Math.abs(x.other.getMean() - x.summary.getMean())));
        const top = sorted?.[0];
        const fCondition = top === undefined ? undefined : getConditionByIndex(top.conditions, 0);
        const rowCount = data.values.reduce((a, b) => a + b.summary.getTotalN() + b.other.getTotalN(), 0);
        let expandText;
        expandText = [];
        sorted
            .filter((s, i) => i > 0 && i < 5)
            .forEach((s, i) => {
            expandText.push(this.getExpandText(data, s, rowCount, i === 3));
        });
        return {
            title: title,
            body: this.getBody(data),
            bodyComponents,
            bullets: this.getDescriptiveBullets(data),
            bulletsComponents,
            columnLabel: lf.getColumnLabel(data.id),
            conditionsLabel: lf.getSetLabel(data.conditions),
            topText: this.getTopText(data, top, rowCount),
            expandText: expandText,
            summaryValue: fCondition !== undefined ? this.labelFactory.getRawValue(fCondition.id, fCondition.value) : undefined
        };
    }
    getExpandText(data, top, rowCount, isLast) {
        let retText = { text: "", column: "" };
        const lf = this.labelFactory;
        if (top === undefined) {
            return retText;
        }
        const outcome = lf.getOutcomeLabel();
        const cLabel = lf.getSetLabel(top.conditions);
        const cOtherLabel = lf.getSetLabel(data.conditions);
        const total = top.summary.getTotalN() + top.other.getTotalN();
        const pctOfData = (total / rowCount) * 100;
        retText.text =
            (isLast ? "and " : "") + cLabel + " (" + pctOfData.toFixed(2) + "% of the data)" + (isLast ? ". " : ", ");
        const condition = getConditionByIndex(top.conditions, 0);
        retText.column = this.labelFactory.getRawValue(condition?.id || 0, condition?.value || 0);
        return retText;
    }
    getTopText(data, top, rowCount) {
        let retText = "";
        const lf = this.labelFactory;
        if (top === undefined) {
            return "";
        }
        const outcome = lf.getOutcomeLabel();
        const cLabel = lf.getSetLabel(top.conditions);
        const cOtherLabel = lf.getSetLabel(data.conditions);
        const total = top.summary.getTotalN() + top.other.getTotalN();
        const pctOfData = (total / rowCount) * 100;
        //For first order charts, use outcome instead of missing cOtherLabel
        if (!(cOtherLabel?.length > 0)) {
            retText =
                "Look at " +
                    cLabel +
                    " (" +
                    pctOfData.toFixed(2) +
                    "% of the data) because " +
                    outcome +
                    " behaves most differently in that case.";
            return retText;
        }
        retText =
            "Look at " +
                cLabel +
                " (" +
                pctOfData.toFixed(2) +
                "% of the data) because " +
                cOtherLabel +
                " behaves most differently in that case.";
        return retText;
    }
    getBody(data) {
        if (conditionsIsEmpty(data.conditions)) {
            return this.getFirstOrderBody(data);
        }
        else {
            return this.getSecondOrderBody(data);
        }
    }
    getBodyComponents(data) {
        const lf = this.labelFactory;
        //first order
        if (conditionsIsEmpty(data.conditions)) {
            return [];
        }
        else {
            //second order
            const result = [
                lf.getColumnLabel(data.id),
                lf.getSetLabel(data.conditions),
                lf.getOtherSetLabel(data.conditions)
            ];
            return result;
        }
    }
    getFirstOrderBody({ values, id }) {
        const lf = this.labelFactory;
        let narrative = "";
        narrative += lf.getOutcomeLabel();
        const sorted = [...values];
        sorted.sort(getNumericComparator((x) => x.summary.getMean()));
        const top = sorted[sorted.length - 1];
        const bottom = sorted[0];
        if (top === undefined) {
            return `All values of ${lf.getColumnLabel(id)} fell below the count cutoff of ${this.minValueCount}.`;
        }
        if (this.metadata.getOutcome().isBinary()) {
            narrative += ` occurs more often when ${lf.getSetLabel(top.conditions)} and less often when ${lf.getSetLabel(bottom.conditions)}.`;
        }
        else {
            narrative += ` is higher when ${lf.getSetLabel(top.conditions)} and lower when ${lf.getSetLabel(bottom.conditions)}.`;
        }
        return narrative;
    }
    getSecondOrderBody(data) {
        const lf = this.labelFactory;
        let narrative = "";
        narrative = `The behavior of ${lf.getColumnLabel(data.id)} changes when ${lf.getSetLabel(data.conditions)}, relative to ${lf.getOtherSetLabel(data.conditions)}.`;
        return narrative;
    }
    getDescriptiveBullets(data) {
        if (conditionsIsEmpty(data.conditions)) {
            return this.getFirstOrderBullets(data);
        }
        else {
            return this.getSecondOrderBullets(data);
        }
    }
    getDescriptiveComponents(data) {
        const descriptiveComponents = [];
        const voteComparator = getVoteComparator(data);
        const valuesVotes = data.values
            .map((value) => this.getModelVotes(value.conditions))
            .filter((votes) => votes.totalCount > 0)
            //.sort(voteComparator.compare)
            .slice(0, 3);
        for (const vote of valuesVotes) {
            descriptiveComponents.push(this.getModelVoteNarrativeComponents(vote));
        }
        return descriptiveComponents;
    }
    getModelVotes(conditions) {
        let positiveCount = 0;
        let totalCount = 0;
        Object.keys(this.features).map((modelName) => {
            this.features[modelName]
                .subset(getConditionIds(conditions))
                .getFeatures(conditions)
                .forEach(({ value }) => {
                if (value > 0) {
                    positiveCount++;
                }
                totalCount++;
            });
        });
        return {
            conditions,
            positiveCount,
            totalCount
        };
    }
    getModelVoteNarrativeComponents(vote) {
        let nModels;
        let modelDirection;
        if (vote.positiveCount > vote.totalCount - vote.positiveCount) {
            nModels = vote.positiveCount;
            modelDirection = "positive";
        }
        else {
            nModels = vote.totalCount - vote.positiveCount;
            modelDirection = "negative";
        }
        const components = [];
        components.push(`${nModels} of ${vote.totalCount}`);
        components.push(this.labelFactory.getSetLabel(vote.conditions));
        components.push(modelDirection);
        components.push(this.labelFactory.getOutcomeLabel());
        return components;
    }
    getModelVoteNarrative(vote) {
        let nModels;
        let modelDirection;
        if (vote.positiveCount > vote.totalCount - vote.positiveCount) {
            nModels = vote.positiveCount;
            modelDirection = "positive";
        }
        else {
            nModels = vote.totalCount - vote.positiveCount;
            modelDirection = "negative";
        }
        return `${nModels} of ${vote.totalCount} models found that ${this.labelFactory.getSetLabel(vote.conditions)} has a ${modelDirection} impact on ${this.labelFactory.getOutcomeLabel()}`;
    }
    getFirstOrderBullets(data) {
        const bullets = [];
        const voteComparator = getVoteComparator(data);
        const valuesVotes = data.values
            .map((value) => this.getModelVotes(value.conditions))
            .filter((votes) => votes.totalCount > 0)
            .sort(voteComparator)
            .slice(0, 3);
        for (const vote of valuesVotes) {
            bullets.push(this.getModelVoteNarrative(vote));
        }
        return bullets;
    }
    getSecondOrderBullets(data) {
        const { values } = data;
        const bullets = [];
        const lf = this.labelFactory;
        const sorted = [...values];
        sorted.sort(getNumericComparator((x) => Math.abs(x.other.getMean() - x.summary.getMean())));
        const notDifferentValues = sorted.filter((x) => !x.isDifferent);
        const directionLabelMap = {
            higher: this.metadata.getOutcome().isBinary() ? "occurs more often" : "is higher",
            lower: this.metadata.getOutcome().isBinary() ? "occurs less often" : "is lower"
        };
        if (notDifferentValues.length > 0) {
            const topSame = notDifferentValues[notDifferentValues.length - 1];
            const direction = topSame.summary.getMean() > topSame.other.getMean() ? "higher" : "lower";
            const percentDelta = Math.abs((topSame.other.getMean() - topSame.summary.getMean()) / topSame.summary.getMean());
            if (percentDelta > 0.1) {
                bullets.push(` Be careful when you look at ${lf.getSetLabel(topSame.conditions)}. Even though ${lf.getOutcomeLabel()} ${directionLabelMap[direction]} here it is not statistically different from ${lf.getOtherSetLabel(data.conditions)}.`);
            }
        }
        const conditionVotes = this.getModelVotes(data.conditions);
        if (conditionVotes.totalCount > 0) {
            bullets.push(this.getModelVoteNarrative(conditionVotes));
        }
        const voteComparator = getVoteComparator(data);
        const valuesVotes = data.values
            .map((value) => this.getModelVotes(value.conditions))
            .filter((votes) => votes.totalCount > 0)
            .sort(voteComparator)
            .slice(0, 3);
        for (const vote of valuesVotes) {
            bullets.push(this.getModelVoteNarrative(vote));
        }
        // I can't think anymore sorry
        let largestModel = undefined;
        let smallestModel = undefined;
        let largestCount = -Infinity;
        let smallestCount = Infinity;
        let isLargePositive = false;
        let isSmallPositive = false;
        Object.keys(this.features).map((modelName) => {
            this.features[modelName]
                .subset(getConditionIds(data.conditions))
                .getFeatures(data.conditions)
                .forEach(({ value }) => {
                if (Math.abs(value) > largestCount) {
                    largestCount = value;
                    largestModel = modelName;
                    if (value > 0) {
                        isLargePositive = true;
                    }
                    else {
                        isLargePositive = false;
                    }
                }
                if (value < smallestCount) {
                    smallestCount = value;
                    smallestModel = modelName;
                    if (value > 0) {
                        isSmallPositive = true;
                    }
                    else {
                        isSmallPositive = false;
                    }
                }
            });
        });
        if (largestModel !== undefined && smallestModel !== undefined) {
            bullets.push(largestModel +
                " saw the largest " +
                (isLargePositive ? "positive" : "negative") +
                " impact and " +
                smallestModel +
                " saw the smallest " +
                (isSmallPositive ? "positive" : "negative") +
                " impact");
        }
        return bullets;
    }
}
class InsightScoreFactory {
    scores;
    interactionScores;
    hasScores;
    hasInteractions;
    metadata;
    dataFactory;
    minValueCount;
    constructor(scores, interactionScores, hasScores, hasInteractions, metadata, dataFactory, minValueCount) {
        this.scores = scores;
        this.interactionScores = interactionScores;
        this.hasScores = hasScores;
        this.hasInteractions = hasInteractions;
        this.metadata = metadata;
        this.dataFactory = dataFactory;
        this.minValueCount = minValueCount;
    }
    isValidCandidate({ ids, conditions }, type) {
        switch (type) {
            case "Descriptive":
                if (this.hasInteractions && ids && conditions) {
                    const allIds = ids.slice();
                    for (const c of conditions) {
                        allIds.push(c.id);
                    }
                    if (allIds.length == 2) {
                        allIds.sort((a, b) => a - b);
                        return (this.interactionScores[allIds.toString()] ?? 0) > 0;
                    }
                }
                return true;
            default:
                return true;
        }
    }
    getScore(data, skipInteractionCheck) {
        switch (data.type) {
            case "Correlation":
                return data.r2;
            case "Descriptive":
                return this.getDescriptiveScore(data, skipInteractionCheck);
            case "ModelDriver":
                return this.getModelDriverScore(data);
            case "Difference":
                return this.getDifferenceScore(data);
            default:
                return 1;
        }
    }
    getModelDriverScore(data) {
        if (conditionsIsEmpty(data.conditions)) {
            const score = this.scores[data.id.toString()];
            if (score !== undefined) {
                return score * 100;
            }
            else {
                return 100;
            }
        }
        else {
            return Math.max(...data.models.flatMap((model) => model.features.map((feature) => Math.abs(feature.value))));
        }
    }
    getDifferenceScore(data) {
        const name = this.metadata.getColumn(data.id).getName();
        const diffId = this.metadata.getColumns().findIndex((c) => c.getName() == `${name}_diff`);
        if (diffId > 0 && !conditionsIsEmpty(data.conditions)) {
            const insightConditions = { ids: [diffId], conditions: data.conditions };
            const diffData = this.dataFactory.getInsightData(insightConditions);
            if (diffData !== undefined) {
                // Skip checking for interactions when getting the score
                return this.getScore(diffData, true);
            }
        }
        const hasNonZeroValue = data.values.map((v) => v.summary.getN()).find((n) => n > 0) !== undefined;
        if (!hasNonZeroValue) {
            return Number.NEGATIVE_INFINITY;
        }
        return 0;
    }
    getDescriptiveCountScore(data) {
        if (conditionsIsEmpty(data.conditions)) {
            return 1;
        }
        return 1;
    }
    getDescriptiveScore(data, skipInteractionCheck) {
        if (this.metadata.getOutcome() instanceof CountOutcomeMetadata) {
            return this.getDescriptiveCountScore(data);
        }
        if (conditionsIsEmpty(data.conditions) && this.hasScores) {
            const score = this.scores[data.id.toString()];
            if (score !== undefined) {
                return score;
            }
        }
        const ids = [data.id, ...data.conditions.map((c) => c.id)].sort((a, b) => a - b);
        const iScore = this.interactionScores[ids.toString()] || 0;
        // If we're calculating the difference score, skip this check because the time column
        // is never going to be in the interactions
        if (!skipInteractionCheck && this.hasInteractions && iScore <= 0) {
            return 0;
        }
        const values = filterDescriptiveValues(this.metadata, data, this.minValueCount);
        if (values.length < data.values.length) {
            const otherValues = data.values.filter(({ summary }) => summary.getTotalN() < this.minValueCount);
            const summary = Summary.addAll(otherValues.map((d) => d.summary));
            const other = Summary.addAll(otherValues.map((d) => d.other));
            values.push({
                conditions: Conditions.emptySet(),
                constraint: Conditions.emptySet(),
                summary: summary,
                other: other,
                isDifferent: false
            });
        }
        if (values.every((x) => !x.isDifferent)) {
            return 0;
        }
        if (values.length <= 1) {
            return NaN;
        }
        const stats = values.flatMap((v) => (v.other.getN() == 0 ? [v.summary] : [v.summary, v.other]));
        const combined = getAdjustedRSquared(stats);
        if (conditionsIsEmpty(data.conditions)) {
            return combined;
        }
        const base = values.map((v) => Summary.addAll([v.summary, v.other]));
        const constraint = Summary.addAll(values.map((v) => v.other));
        const cross = [constraint, Summary.addAll(base).subtract(constraint)];
        const separate = Math.max(getAdjustedRSquared(base), getAdjustedRSquared(cross));
        return combined - separate;
    }
    getInformationScore(data) {
        const ids = [data.id].concat(getConditionIds(data.conditions));
        ids.sort((a, b) => a - b);
        const score = this.scores[ids.toString()];
        if (score !== undefined) {
            return score;
        }
        return 0;
    }
    static create(metadata, inclusionProbabilities, interactionInclusionProbabilities, dataFactory, minValueCount) {
        const csf = metadata.getConditionSetFactory();
        const scores = {};
        const fields = metadata.getColumns().map((c) => c.getName());
        fields.forEach((fieldOne) => fields.forEach((fieldTwo) => {
            const id = csf.getId(fieldOne);
            const currKeys = [fieldOne, fieldTwo];
            if (id !== undefined) {
                scores[id.toString()] =
                    (interactionInclusionProbabilities.find((prob) => {
                        return currKeys.every((k) => prob?.keys?.includes(k)) && prob?.keys?.every((k) => currKeys.includes(k));
                    })?.value ?? 0) + (scores[id.toString()] ?? 0);
            }
        }));
        const hasScores = Object.values(scores).some((x) => x != 0);
        const interactionScores = {};
        let hasInteractions = false;
        interactionInclusionProbabilities.forEach((prob) => {
            const ids = prob.keys
                .map((k) => csf.getId(k))
                .filter((id) => id !== undefined)
                .sort((a, b) => {
                if (a !== undefined && b !== undefined) {
                    return a - b;
                }
                return 0;
            });
            if (ids.length >= 2) {
                hasInteractions = hasInteractions || prob.keys.some((k1) => prob.keys.some((k2) => k1 !== k2));
                interactionScores[ids.toString()] = prob.value;
            }
        });
        return new InsightScoreFactory(scores, interactionScores, hasScores, hasInteractions, metadata, dataFactory, minValueCount);
    }
}
class InsightConditionsFactory {
    conditionSetFactory;
    constructor(conditionSetFactory) {
        this.conditionSetFactory = conditionSetFactory;
    }
    getBuilder() {
        return new InsightConditionsBuilder(this.conditionSetFactory);
    }
    getInsightConditions(input) {
        if (input === undefined) {
            return { conditions: [] };
        }
        else if (isInsightMetadata(input)) {
            return this.getInsightConditionsFromProperties(input.properties);
        }
        else {
            return this.getInsightConditionsFromQuery(input);
        }
    }
    getInsightConditionsFromQuery(query) {
        const columns = [query.column].flat().filter(filterUndefined);
        const ids = columns.map((c) => this.conditionSetFactory.getId(c)).filter(filterUndefined);
        const conditions = this.conditionSetFactory.getConditionSet(query.filters);
        return {
            ids: ids,
            conditions: conditions
        };
    }
    getInsightConditionsFromProperties(properties) {
        return properties.reduce((builder, property) => builder.addProperty(property), this.getBuilder()).build();
    }
    static create(metadata) {
        return new InsightConditionsFactory(metadata.getConditionSetFactory());
    }
}
